import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Hands the add-expense sheet's ways-in dock to the native side, so it renders
// in the same Liquid Glass as the dashboard bar instead of a CSS approximation
// of it. Lives on the sheet element alongside `expense-deck`, which stays the
// single source of truth for which way in is selected — this controller only
// mirrors that decision to native and relays native taps back into it.
//
// The web dock is NOT removed. It keeps its place in the sheet's flex column at
// `visibility: hidden`, so the layout still reserves the capsule's 58px and the
// native view is pinned into exactly the gap the web left for it. Deleting it
// would collapse that gap and the native dock would sit on top of the card.
//
// Usage — both controllers on the sheet, the bridge one only in the native app:
//
//   <div data-controller="expense-deck bridge--expense-dock"
//        data-action="expense-deck:tabchange->bridge--expense-dock#onTabChange">
//     …
//     <button data-bridge--expense-dock-target="tab"
//             data-icon="microphone-fill" data-symbol="mic.fill"> … </button>
export default class extends BridgeComponent {
  static component = "expense-dock"
  static targets = ["tab"]

  connect() {
    super.connect()

    // Hide the web capsule synchronously rather than waiting for the native
    // "connected" reply: the round trip is long enough to show both.
    this.#concealWebDock()

    requestAnimationFrame(() => {
      this.send("connect", {
        tabs: this.#tabs(),
        selectedIndex: this.#deck?.indexValue ?? 0,
        locale: document.documentElement.lang || "en"
      }, (message) => this.#handleCallback(message))
    })
  }

  disconnect() {
    super.disconnect()
    this.#revealWebDock()
  }

  // ── Called from the page ───────────────────────────────────────────────────

  // A swipe across the deck changed the panel, so the native thumb has to move
  // with it. Fired by expense-deck's own tabchange event.
  onTabChange(event) {
    const index = Number(event.detail?.index)
    if (Number.isNaN(index)) return
    this.send("select", { index })
  }

  // Called by voice-modal when the sheet opens / when the input area returns.
  show() {
    this.send("show", {})
  }

  // Called by voice-modal when the sheet closes, when a drag commits, and
  // whenever the input area gives way to loading / confirm / consent.
  hide() {
    this.send("hide", {})
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #handleCallback(message) {
    if (message.event !== "tab_tapped") return

    const index = Number(message.data?.index)
    if (Number.isNaN(index)) return

    // Straight into the deck rather than clicking the hidden web button: the
    // button carries a bridge--haptic action, and the native dock has already
    // played that tap's haptic itself. A synthetic click would double it.
    this.#deck?.selectIndex(index)
  }

  #tabs() {
    return this.tabTargets.map((tab) => ({
      icon: tab.dataset.icon || "",
      symbol: tab.dataset.symbol || "questionmark",
      label: (tab.querySelector("span")?.textContent || "").trim()
    }))
  }

  get #deck() {
    return this.application.getControllerForElementAndIdentifier(this.element, "expense-deck")
  }

  get #wrap() {
    return this.element.querySelector(".expense-dock-wrap")
  }

  #concealWebDock() {
    const wrap = this.#wrap
    if (wrap) wrap.style.visibility = "hidden"
  }

  #revealWebDock() {
    const wrap = this.#wrap
    if (wrap) wrap.style.visibility = ""
  }
}
